import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { useAssignBewerber } from '../../api/bewerber';
import { useUserList } from '../../api/users';
import type { AssignBewerberDto } from '../../types/bewerber';

interface ZurKlaerungModalProps {
  isOpen: boolean;
  onClose: () => void;
  bewerberId: string;
  bewerberName: string;
}

export function ZurKlaerungModal({
  isOpen,
  onClose,
  bewerberId,
  bewerberName,
}: ZurKlaerungModalProps) {
  const assignMutation = useAssignBewerber();
  const { data: usersData, isLoading: isLoadingUsers } = useUserList({ isActive: true, limit: 100 });
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<AssignBewerberDto>({
    defaultValues: {
      userId: '',
      comment: '',
    },
  });

  const handleFormSubmit = async (data: AssignBewerberDto) => {
    setError(null);
    try {
      await assignMutation.mutateAsync({
        id: bewerberId,
        data,
      });
      toast.success('Bewerber erfolgreich zur Klärung zugewiesen');
      reset();
      onClose();
    } catch (err) {
      setError('Fehler beim Zuweisen des Bewerbers');
      toast.error('Fehler beim Zuweisen des Bewerbers');
      console.error('Failed to assign bewerber:', err);
    }
  };

  const handleClose = () => {
    reset();
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  const activeUsers = usersData?.data || [];

  return createPortal(
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={handleClose}
        />

        {/* Modal */}
        <div className="relative inline-block bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:max-w-lg sm:w-full">
          <form onSubmit={handleSubmit(handleFormSubmit)}>
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 sm:mx-0 sm:h-10 sm:w-10">
                  <svg className="h-6 w-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left flex-1">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Zur Klärung zuweisen
                  </h3>
                  <p className="mt-2 text-sm text-gray-500">
                    Weisen Sie <span className="font-medium">{bewerberName}</span> einem Mitarbeiter zur Klärung zu.
                  </p>

                  {error && (
                    <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded-md">
                      <p className="text-sm text-red-600">{error}</p>
                    </div>
                  )}

                  <div className="mt-4 space-y-4">
                    <div>
                      <label htmlFor="userId" className="block text-sm font-medium text-gray-700">
                        Zuweisen an *
                      </label>
                      <select
                        id="userId"
                        {...register('userId', { required: 'Bitte wählen Sie einen Mitarbeiter' })}
                        disabled={isLoadingUsers}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm disabled:bg-gray-100"
                      >
                        <option value="">
                          {isLoadingUsers ? 'Lade Mitarbeiter...' : '-- Mitarbeiter auswählen --'}
                        </option>
                        {activeUsers.map((user) => (
                          <option key={user.id} value={user.id}>
                            {user.firstName} {user.lastName} ({user.email})
                          </option>
                        ))}
                      </select>
                      {errors.userId && (
                        <p className="mt-1 text-sm text-red-600">{errors.userId.message}</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="comment" className="block text-sm font-medium text-gray-700">
                        Kommentar *
                      </label>
                      <textarea
                        id="comment"
                        rows={3}
                        {...register('comment', {
                          required: 'Bitte geben Sie einen Kommentar ein',
                          minLength: { value: 10, message: 'Kommentar muss mindestens 10 Zeichen haben' },
                        })}
                        placeholder="Beschreiben Sie, was geklärt werden muss..."
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                      />
                      {errors.comment && (
                        <p className="mt-1 text-sm text-red-600">{errors.comment.message}</p>
                      )}
                      <p className="mt-1 text-xs text-gray-500">
                        Dieser Kommentar wird als Notiz gespeichert und der zugewiesene Mitarbeiter per E-Mail benachrichtigt.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                type="submit"
                disabled={assignMutation.isPending || isLoadingUsers}
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-yellow-600 text-base font-medium text-white hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
              >
                {assignMutation.isPending ? 'Wird zugewiesen...' : 'Zuweisen'}
              </button>
              <button
                type="button"
                onClick={handleClose}
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              >
                Abbrechen
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>,
    document.body
  );
}
